import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { DSButton } from "@components/design-system/molecules";
import {
  useAuth,
  useOrganization,
  useTeam,
  useTemplates,
} from "@hooks";
import { Tooltip } from "@mui/material";
import mixpanel from "mixpanel-browser";
import { useState } from "react";

import TeamDefaultModal from "./TeamDefaultModal";
import {
  SET_TEAM_DEFAULT_TEMPLATE,
  REMOVE_TEAM_DEFAULT_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import { trackOperation } from "../../utils/operationTracking";

export const UpdateTeamDefaultButton = ({
  template,
  onSuccess,
  disabled = false,
  source,
  disabledReason = "",
}) => {
  const { userTeams } = useTeam();
  const { userUuid } = useAuth();
  const { isOrgAdmin, orgUuid } = useOrganization();
  const { refetchTeamDefaults, defaultTemplatesForSelectedOrg } =
    useTemplates();
  const [showTeamDefaultModal, setShowTeamDefaultModal] =
    useState(false);

  const [setTeamDefault] = useMutation(SET_TEAM_DEFAULT_TEMPLATE);
  const [removeTeamDefault] = useMutation(
    REMOVE_TEAM_DEFAULT_TEMPLATE,
  );

  const adminTeams = (userTeams || []).filter((team) =>
    team.users?.some((u) => u.uuid === userUuid && u.isAdmin),
  );

  const ownsTemplate = template?.user?.uuid === userUuid;

  const canSetTeamDefault = template?.sharingType === "TEAM";

  const isOrgDefault = !!defaultTemplatesForSelectedOrg?.some(
    (t) => t.uuid === template?.uuid,
  );

  const isOrgDefaultAndNotAdmin = isOrgDefault && !isOrgAdmin;

  const isTeamDefault =
    template && adminTeams?.length
      ? adminTeams.some((team) =>
          template.teamDefaults?.includes(team.uuid),
        )
      : false;

  const getButtonText = () => {
    if (isOrgDefaultAndNotAdmin) {
      return "Default for all organization teams";
    }

    if (!canSetTeamDefault) {
      return "Only team-shared templates can be defaults";
    }

    const hasMultipleTeams = adminTeams?.length > 1;

    if (hasMultipleTeams) {
      return "Edit Team Default Settings";
    }

    return isTeamDefault
      ? "Remove from My Team Defaults"
      : "Edit Team Default Settings";
  };

  const getDisabledReason = () => {
    if (disabledReason) return disabledReason;
    if (isOrgDefaultAndNotAdmin) {
      return "This template is set as a default for all teams within your organization and cannot be modified at the individual team level";
    }
    return "";
  };

  const handleClick = async () => {
    if (!template) return;

    if (template.sharingType !== "TEAM" && !ownsTemplate) {
      alert(
        "error",
        "Only team-shared templates or your own templates can be set as team defaults",
      );
      return;
    }

    const hasMultipleTeams = adminTeams?.length > 1;

    // If only one admin team, directly toggle without opening modal
    if (!hasMultipleTeams && adminTeams?.length === 1) {
      const teamUuid = adminTeams[0].uuid;
      const isCurrentlyDefault =
        template.teamDefaults?.includes(teamUuid);

      await trackOperation(
        "template-team-default-toggled",
        async (ctx) => {
          const beforeTeamDefaults = template.teamDefaults || [];
          const afterTeamDefaults = isCurrentlyDefault
            ? beforeTeamDefaults.filter((uuid) => uuid !== teamUuid)
            : [...beforeTeamDefaults, teamUuid];

          ctx.addProperties({
            templateUuid: template.uuid,
            templateOwnerUuid: template.user?.uuid,
            beforeTeamDefaults,
            afterTeamDefaults,
            action: isCurrentlyDefault ? "remove" : "add",
            source,
          });

          if (isCurrentlyDefault) {
            await removeTeamDefault({
              variables: {
                templateUuid: template.uuid,
                teamUuids: [teamUuid],
              },
            });
            alert("success", "Template removed from team defaults.");
          } else {
            await setTeamDefault({
              variables: {
                templateUuid: template.uuid,
                teamUuids: [teamUuid],
              },
            });
            alert("success", "Template set as team default.");
          }

          refetchTeamDefaults();
          if (onSuccess) {
            onSuccess();
          }
        },
      );
    } else {
      mixpanel.track("Multi-Team Default Modal Opened", {
        templateUuid: template.uuid,
        templateOwnerUuid: template.user?.uuid,
        currentTeamDefaults: template.teamDefaults || [],
        orgDefault: isOrgDefault ? orgUuid : null,
        source,
      });
      setShowTeamDefaultModal(true);
    }
  };

  if (adminTeams.length === 0) {
    return null;
  }

  if (!canSetTeamDefault) {
    return null;
  }

  return (
    <>
      <Tooltip arrow placement="top" title={getDisabledReason()}>
        <span className="ml-auto">
          <DSButton
            type="button"
            variant="secondary"
            onClick={handleClick}
            disabled={
              disabled ||
              !canSetTeamDefault ||
              isOrgDefaultAndNotAdmin
            }
            dataTestId="update-team-default-button"
            size="sm"
          >
            {getButtonText()}
          </DSButton>
        </span>
      </Tooltip>

      {showTeamDefaultModal && (
        <TeamDefaultModal
          template={template}
          adminTeams={adminTeams}
          onClose={() => setShowTeamDefaultModal(false)}
          onSuccess={() => {
            setShowTeamDefaultModal(false);
            if (onSuccess) {
              onSuccess();
            }
          }}
          source={source}
        />
      )}
    </>
  );
};
